import {
	type CalendarConnectorType,
	fetchEnabledConnectorTypes,
} from '@atlassian/home-calendar-widget/fetch-connectors';
import type { ThirdPartyConnectorsResult } from '@atlassian/home-calendar-widget/with-preloaded-connectors';

import { createResource } from '@townsquare/router/resources/createResource';
import type { RouteResource } from '@townsquare/router/resources/types';
import type { ResourceContext } from '@townsquare/router/types';
import { useResource } from '@townsquare/router/useResource';
import { fg } from '@townsquare/stat-sig/gate';

export const calendarConnectorsResource: RouteResource<CalendarConnectorType[]> = createResource<
	CalendarConnectorType[]
>({
	type: 'CALENDAR-CONNECTORS',
	getKey: (_, resourceContext) => {
		if (!fg('townsquare_calendar_widget_preload')) {
			return 'CALENDAR-CONNECTORS-FG-OFF';
		}

		const { collabContext } = resourceContext as ResourceContext;

		const cloudId = collabContext?.rovoWorkspace;

		return `CALENDAR-CONNECTORS-${cloudId}`;
	},
	getData: async (_, resourceContext) => {
		if (!fg('townsquare_calendar_widget_preload')) {
			return [];
		}

		const { collabContext } = resourceContext as ResourceContext;

		const cloudId = collabContext?.rovoWorkspace;

		if (!cloudId) {
			return [];
		}

		return fetchEnabledConnectorTypes(cloudId);
	},
});

export type CalendarConnectorsResult = ThirdPartyConnectorsResult & { error?: Error };

export const useCalendarConnectors = (): CalendarConnectorsResult => {
	const { loading, data, error } = useResource(calendarConnectorsResource);

	if (!fg('townsquare_calendar_widget_preload')) {
		return {
			enabledTypes: [],
			loading: false,
			hasError: false,
		};
	}

	if (error) {
		return {
			enabledTypes: [],
			loading: false,
			hasError: true,
			error: error instanceof Error ? error : new Error(String(error)),
		};
	} else if (loading || !data) {
		return {
			enabledTypes: [],
			loading: true,
			hasError: false,
		};
	}

	return {
		enabledTypes: data,
		loading: false,
		hasError: false,
	};
};
